import { useState } from 'react'

interface ExportKind {
  id: string
  name: string
  summary: string
  /** What the date range filters on. `null` means there's no date dimension — always the current snapshot. */
  dateBasis: string | null
}

const KINDS: ExportKind[] = [
  {
    id: 'messages',
    name: 'Messages',
    summary: 'Every message, inbound and outbound, with who sent it and to which number.',
    dateBasis: 'when the message was sent',
  },
  {
    id: 'conversations',
    name: 'Conversations',
    summary: 'One row per chat, with its labels, contact and unread count.',
    dateBasis: 'when the chat started',
  },
  {
    id: 'tickets',
    name: 'Tickets',
    summary: 'Every ticket with its category, priority, reply target and how it was resolved.',
    dateBasis: 'when the ticket was opened',
  },
  {
    id: 'contacts',
    name: 'Contacts',
    summary: 'Everyone who has messaged you, with their tags.',
    dateBasis: 'when the contact was added',
  },
  {
    id: 'broadcasts',
    name: 'Bulk send results',
    summary: 'Each recipient of each send, whether it arrived, and why not if it did not.',
    dateBasis: 'when the send was created',
  },
  {
    id: 'ticket-history',
    name: 'Ticket history',
    summary: 'One row per day: how many tickets opened, how many closed, and the running backlog.',
    dateBasis: 'the day of each row (defaults to the last 30 days if no range is set)',
  },
  {
    id: 'chat-actions',
    name: 'Chat actions',
    summary: 'A group’s membership and role changes — who was added, removed, or made admin, and by whom.',
    dateBasis: 'when the change happened',
  },
  {
    id: 'members',
    name: 'Members',
    summary: 'Your team’s roster — role and how much of the inbox each person can see. Admins only.',
    dateBasis: null,
  },
  {
    id: 'phones',
    name: 'Phones',
    summary: 'Every connected WhatsApp number — its status, last connection, and how much history was imported.',
    dateBasis: null,
  },
  {
    id: 'tasks',
    name: 'Tasks',
    summary: 'Every task with its status, priority, assignee, and the chat it is attached to, if any.',
    dateBasis: 'when the task was created',
  },
  {
    id: 'scheduled-messages',
    name: 'Scheduled messages',
    summary: 'Every message queued to send later — one-off or recurring — with its status and any send error.',
    dateBasis: 'when the send is scheduled for',
  },
]

/**
 * Taking the data out.
 *
 * A plain link rather than a fetch: the file is streamed straight from the worker and the
 * browser's own download machinery handles a large one far better than anything built here
 * would, including showing progress and surviving a tab being closed.
 *
 * What comes out respects the same label scope as the inbox — an export is a read, and it
 * would be a poor access model that could be walked around by clicking Download.
 */
export function Exports() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv')
  const [schema, setSchema] = useState<'simplified' | 'legacy'>('simplified')

  const query = new URLSearchParams()
  if (from) query.set('from', from)
  if (to) query.set('to', to)
  if (format === 'xlsx') query.set('format', 'xlsx')
  if (schema === 'legacy') query.set('schema', 'legacy')
  const suffix = query.toString() ? `?${query}` : ''

  const rangeLabel = from || to ? `${from || 'the beginning'} to ${to || 'today'}` : 'everything'

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Your data</span>
        <h1 className="cad-title">Exports</h1>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Everything here is yours and leaves as a plain CSV that opens in any spreadsheet.
          You only get what you can already see in the app, so a teammate limited to certain
          labels exports only those conversations. Past 5,000 rows, the file is handed off
          rather than downloaded straight away — you'll get a link, and an email too if this
          workspace has one configured.
        </p>
      </header>

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">Limit to a period</h2>
          <div className="field-grid">
            <div className="cad-field">
              <label htmlFor="export-from">From</label>
              <input
                id="export-from"
                className="cad-input"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="cad-field">
              <label htmlFor="export-to">To</label>
              <input
                id="export-to"
                className="cad-input"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="cad-field" style={{ justifyContent: 'flex-end' }}>
              {(from || to) && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="quiet"
                  onClick={() => {
                    setFrom('')
                    setTo('')
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <p className="cad-note">
            Downloading <b>{rangeLabel}</b>. Both ends are included, and the dates are read in
            UTC so an export is the same file wherever it is run from.
          </p>

          <div className="row gap-4 wrap" style={{ marginTop: '4px' }}>
            <div className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>File format</span>
              <div className="row gap-2">
                {(['csv', 'xlsx'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="label-swatch"
                    aria-pressed={format === option}
                    data-selected={format === option}
                    onClick={() => setFormat(option)}
                  >
                    {option === 'csv' ? 'CSV' : 'Excel'}
                  </button>
                ))}
              </div>
            </div>
            <div className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Column names</span>
              <div className="row gap-2">
                {(['simplified', 'legacy'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="label-swatch"
                    aria-pressed={schema === option}
                    data-selected={schema === option}
                    onClick={() => setSchema(option)}
                  >
                    {option === 'simplified' ? 'Simplified' : 'Legacy'}
                  </button>
                ))}
              </div>
              {schema === 'legacy' && (
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                  Renamed columns over the same data — for a tool built against another
                  product's export. Not every kind has a legacy alias; those fall back to the
                  usual names.
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="cad-grid-cards">
        {KINDS.map((kind) => (
          <section key={kind.id} className="cad-card">
            <div className="stack gap-3">
              <div className="stack gap-1">
                <h3 className="cad-section-title">{kind.name}</h3>
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                  {kind.summary}
                </p>
              </div>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                {kind.dateBasis ? `Dates filter on ${kind.dateBasis}.` : 'Not date-filtered — always the current snapshot.'}
              </span>
              <div>
                <a className="cad-btn" href={`/api/exports/${kind.id}${suffix}`} download>
                  Download {format === 'xlsx' ? 'Excel' : 'CSV'}
                </a>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="cad-card">
        <div className="stack gap-2">
          <h2 className="cad-section-title">A note on opening these</h2>
          <p className="cad-note">
            Message text can contain anything a customer typed, including things a spreadsheet
            would otherwise treat as a formula. Those cells are written as plain text, so what
            you see in the file is exactly what was sent — a leading apostrophe you may notice
            in the formula bar is what keeps it that way.
          </p>
        </div>
      </section>
    </>
  )
}
